import { Component, Input, Output, EventEmitter, signal, inject, OnInit, computed } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SelectModule } from 'primeng/select';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { MessageService } from 'primeng/api';
import { Router } from '@angular/router';
import { UserSettingsService, UserSettings } from '../../core/services/user-settings.service';
import { LanguageInitService } from '../../core/services/language-init.service';
import { AuthService } from '../../core/services/auth.service';
import { AppButtonComponent } from '../../shared/components/app-button/app-button.component';

// Language interface for component use
interface Language {
  code: string;
  name: string;
  flag: string;
}

@Component({
  selector: 'app-user-settings',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    SelectModule,
    ToastModule,
    ToggleSwitchModule,
    AppButtonComponent
  ],
  providers: [MessageService],
  templateUrl: './user-settings.component.html',
  styleUrls: ['./user-settings.component.css']
})
export class UserSettingsComponent implements OnInit {
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private userSettingsService = inject(UserSettingsService);
  private languageInitService = inject(LanguageInitService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private location = inject(Location);

  @Input() availableLanguages: Language[] = [
    { code: 'en', name: 'English', flag: '🇺🇸' },
    { code: 'vi', name: 'Tiếng Việt', flag: '🇻🇳' }
  ];

  @Output() settingsChanged = new EventEmitter<UserSettings>();
  @Output() closeSettings = new EventEmitter<void>();
  @Output() languageChanged = new EventEmitter<string>();

  currentSettings = signal<UserSettings>(this.userSettingsService.userSettings);

  isSaving = signal(false);
  hasChanges = signal(false);

  selectedLanguageCode = signal<string>('en');

  showUnavailableMenuItems = signal(false);
  showTenantSwitcher = signal(false);

  readonly isAdminUser = computed(() => {
    const profileRole = (this.userSettingsService.userProfile?.role || '').toLowerCase();
    return (
      this.authService.isAdminOrSuperAdmin() ||
      profileRole === 'administrator' ||
      profileRole === 'admin'
    );
  });

  private originalLanguage = 'en';

  private originalShowUnavailableMenuItems = false;
  private originalShowTenantSwitcher = false;

  ngOnInit(): void {
    this.ensureUserSettingsTranslations();
    this.userSettingsService.initialize();
    this.originalShowUnavailableMenuItems =
      this.userSettingsService.showUnavailableMenuItems();
    this.showUnavailableMenuItems.set(this.originalShowUnavailableMenuItems);
    this.originalShowTenantSwitcher =
      this.userSettingsService.showTenantSwitcher();
    this.showTenantSwitcher.set(this.originalShowTenantSwitcher);
    const currentI18nLanguage = this.translateService.currentLang || 'en';
    this.originalLanguage = currentI18nLanguage;

    this.currentSettings.update(settings => ({
      ...settings,
      language: currentI18nLanguage
    }));

    const currentLanguageInfo = this.availableLanguages.find(lang => lang.code === currentI18nLanguage);
    this.selectedLanguageCode.set(currentLanguageInfo?.code ?? 'en');
  }

  openTenantManagement(): void {
    this.router.navigateByUrl('/platform/tenants');
  }

  openTenantPackages(): void {
    this.router.navigateByUrl('/platform/packages');
  }

  openPlatformModules(): void {
    this.router.navigateByUrl('/platform/modules');
  }

  onLanguageChange(event: any): void {
    const languageCode = event.value;

    const selectedLanguageObj = this.availableLanguages.find(lang => lang.code === languageCode);
    if (selectedLanguageObj) {
      this.selectedLanguageCode.set(languageCode);
    }

    this.currentSettings.update(settings => ({
      ...settings,
      language: languageCode
    }));

    this.hasChanges.set(true);
  }

  onShowUnavailableMenuItemsChange(value: boolean): void {
    this.showUnavailableMenuItems.set(value);
    this.hasChanges.set(true);
  }

  onShowTenantSwitcherChange(value: boolean): void {
    this.showTenantSwitcher.set(value);
    this.hasChanges.set(true);
    // Persist immediately so sidebar reacts live, and also reset to Tenant #1 when turned off
    this.userSettingsService.updateUserPreferences({
      showTenantSwitcher: value,
    });
    if (!value) {
      this.authService.setActiveTenantId('1', true);
    }
  }

  onSaveChanges(): void {
    if (!this.hasChanges()) {
      this.messageService.add({
        severity: 'warn',
        summary: this.translateService.instant('user_settings.messages.no_changes'),
        detail: this.translateService.instant('user_settings.messages.no_changes_detail'),
        life: 3000
      });
      return;
    }

    this.isSaving.set(true);

    try {
      this.userSettingsService.updateUserSettings({
        ...this.currentSettings(),
        showTenantSwitcher: this.showTenantSwitcher(),
      });
      this.userSettingsService.updateUserPreferences({
        showUnavailableMenuItems: this.showUnavailableMenuItems(),
        showTenantSwitcher: this.showTenantSwitcher(),
      });
      if (!this.showTenantSwitcher()) {
        this.authService.setActiveTenantId('1', true);
      }
      this.settingsChanged.emit(this.currentSettings());
      this.hasChanges.set(false);

      this.languageInitService.changeLanguage(this.currentSettings().language);
      this.languageChanged.emit(this.currentSettings().language);

      this.messageService.add({
        severity: 'success',
        summary: this.translateService.instant('user_settings.messages.settings_saved'),
        detail: this.translateService.instant('user_settings.messages.settings_saved_detail'),
        life: 3000
      });

      setTimeout(() => {
        this.location.back();
      }, 1000);

    } catch (error) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('user_settings.messages.save_error'),
        detail: this.translateService.instant('user_settings.messages.save_error_detail'),
        life: 5000
      });
    } finally {
      this.isSaving.set(false);
    }
  }

  onCancel(): void {
    if (this.hasChanges()) {
      this.currentSettings.update(settings => ({
        ...settings,
        language: this.originalLanguage
      }));

      const originalLanguageInfo = this.availableLanguages.find(lang => lang.code === this.originalLanguage);
      if (originalLanguageInfo) {
        this.selectedLanguageCode.set(originalLanguageInfo.code);
      }

      this.showUnavailableMenuItems.set(this.originalShowUnavailableMenuItems);
      this.showTenantSwitcher.set(this.originalShowTenantSwitcher);
      this.userSettingsService.updateUserPreferences({
        showTenantSwitcher: this.originalShowTenantSwitcher,
      });
      this.hasChanges.set(false);

      this.languageInitService.changeLanguage(this.originalLanguage);
      this.languageChanged.emit(this.originalLanguage);

      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('user_settings.messages.changes_cancelled'),
        detail: this.translateService.instant('user_settings.messages.changes_cancelled_detail'),
        life: 3000
      });
    }

    this.closeSettings.emit();
    this.location.back();
  }

  getLanguageDisplayName(language: Language): string {
    return `${language.flag} ${language.name}`;
  }

  private ensureUserSettingsTranslations(): void {
    const enDefaults = {
      user_settings: {
        title: 'User Settings',
        sections: {
          language: 'Language Settings',
          menu_display: 'Menu Display',
          multi_tenancy: 'Multi-Tenancy & Deployment Mode',
        },
        descriptions: {
          language: 'Choose your preferred language',
          show_unavailable_menu_items: 'Show unavailable menu items',
          show_tenant_switcher: 'Show Multi-Tenant Switcher in Sidebar',
          tenant_crud_management: 'Multi-Tenancy CRUD & Package Administration',
        },
        hints: {
          show_unavailable_menu_items:
            'Unavailable items appear grayed out and cannot be opened.',
          show_tenant_switcher:
            'Default OFF for single-tenant or on-premise deployments. Enable to switch between retail tenants in the sidebar.',
          tenant_crud_management:
            'Create, edit, or delete Retail Tenants, configure Tenant Packages, and manage Module Subscriptions.',
        },
        actions: {
          save_changes: 'Save Changes',
          cancel: 'Cancel',
          saving: 'Saving...',
          manage_tenants: 'Manage Tenants (CRUD)',
          manage_tenant_packages: 'Tenant Packages',
          manage_platform_modules: 'Module Subscriptions',
        },
        messages: {
          settings_saved: 'Settings Saved',
          settings_saved_detail: 'Your settings have been successfully saved',
          no_changes: 'No Changes',
          no_changes_detail: 'There are no changes to save.',
          save_error: 'Save Failed',
          save_error_detail: 'An error occurred while saving your settings.',
          changes_cancelled: 'Changes Cancelled',
          changes_cancelled_detail: 'Your unsaved changes have been reverted.',
        },
      },
    };
    const viDefaults = {
      user_settings: {
        title: 'Cài đặt người dùng',
        sections: {
          language: 'Cài đặt ngôn ngữ',
          menu_display: 'Hiển thị menu',
          multi_tenancy: 'Đa nhánh thuê bao (Multi-Tenancy)',
        },
        descriptions: {
          language: 'Chọn ngôn ngữ ưa thích của bạn',
          show_unavailable_menu_items: 'Hiển thị các mục menu chưa khả dụng',
          show_tenant_switcher:
            'Hiển thị bộ chuyển đổi Tenant trên thanh điều hướng',
          tenant_crud_management: 'Quản trị Tenant (CRUD) & Gói thuê bao',
        },
        hints: {
          show_unavailable_menu_items:
            'Các mục chưa khả dụng sẽ hiển thị màu xám và không thể mở.',
          show_tenant_switcher:
            'Mặc định TẮT cho triển khai On-Premise hoặc một doanh nghiệp. Bật để chuyển đổi giữa các Tenant trên thanh điều hướng.',
          tenant_crud_management:
            'Tạo mới, chỉnh sửa, xóa Tenant, cấu hình Gói thuê bao (Tenant Packages) và phân quyền Module.',
        },
        actions: {
          save_changes: 'Lưu thay đổi',
          cancel: 'Hủy bỏ',
          saving: 'Đang lưu...',
          manage_tenants: 'Quản lý Tenant (CRUD)',
          manage_tenant_packages: 'Gói thuê bao',
          manage_platform_modules: 'Đăng ký Module',
        },
        messages: {
          settings_saved: 'Đã lưu cài đặt',
          settings_saved_detail: 'Cài đặt của bạn đã được lưu thành công',
          no_changes: 'Không có thay đổi',
          no_changes_detail: 'Không có thay đổi nào để lưu.',
          save_error: 'Lưu thất bại',
          save_error_detail: 'Đã xảy ra lỗi khi lưu cài đặt của bạn.',
          changes_cancelled: 'Đã hủy thay đổi',
          changes_cancelled_detail:
            'Các thay đổi chưa lưu của bạn đã được hoàn tác.',
        },
      },
    };
    this.translateService.setTranslation('en', enDefaults, true);
    this.translateService.setTranslation('vi', viDefaults, true);
  }
}
